import { GlobalExecutionsCache } from "./types";

const CACHE_KEY_PREFIX = "global-job-executions-cache-";
const CACHE_VERSION = "v1";

function getCacheKey(orgName: string): string {
  return `${CACHE_KEY_PREFIX}${CACHE_VERSION}-${orgName}`;
}

export function loadCache(orgName: string): GlobalExecutionsCache | null {
  try {
    const key = getCacheKey(orgName);
    const cached = localStorage.getItem(key);
    if (!cached) {
      return null;
    }
    const parsed = JSON.parse(cached) as GlobalExecutionsCache;
    // Validate cache matches current org
    if (parsed.orgName !== orgName) {
      return null;
    }
    return parsed;
  } catch (e) {
    console.error("Failed to load global executions cache:", e);
    return null;
  }
}

export function saveCache(orgName: string, cache: Omit<GlobalExecutionsCache, "orgName">): void {
  try {
    const key = getCacheKey(orgName);
    const fullCache: GlobalExecutionsCache = {
      ...cache,
      orgName,
    };
    localStorage.setItem(key, JSON.stringify(fullCache));
  } catch (e) {
    console.error("Failed to save global executions cache:", e);
  }
}

export function clearCache(orgName: string): void {
  try {
    const key = getCacheKey(orgName);
    localStorage.removeItem(key);
  } catch (e) {
    console.error("Failed to clear global executions cache:", e);
  }
}

export function getCacheAge(cache: GlobalExecutionsCache | null): string {
  if (!cache || !cache.lastFetched) {
    return "Never";
  }

  const lastFetched = new Date(cache.lastFetched);
  const now = new Date();
  const diffMs = now.getTime() - lastFetched.getTime();
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 1) {
    return "Just now";
  }
  if (diffMins < 60) {
    return `${diffMins} minute${diffMins === 1 ? "" : "s"} ago`;
  }
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) {
    return `${diffHours} hour${diffHours === 1 ? "" : "s"} ago`;
  }
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays} day${diffDays === 1 ? "" : "s"} ago`;
}
